import PendingGuest from '@/components/notifications/PendingGuest.vue';

const state = {
  areaId: null,
  roomId: null,
  roomName: '',
  confType: '', //conference type of room (spacein/link/etc)
  passwordRequired: false,
  isLeaving: false,
  isKicked: false,
  isWaiting: false, //on hold
  isInvalid: false,
  invalidReason: null,
  rejected: false, //if rejected from lobby

  // The current pending guest if we're showing a popup to the members of a Room to accept/decline a guest
  currentPendingGuest: null
};

const getters = {
  areaId: (state) => state.areaId,
  roomId: (state, getters, rootState, rootGetters) => {
    if (state.roomId && state.roomId !== 'undefined') return state.roomId;
    else if (rootGetters['routing/getParamValue']('roomId')) return rootGetters['routing/getParamValue']('roomId');
    else {
      // we check against localStorage too because this might be the first load
      let storedId = localStorage.getItem('guestInviteRoomId');
      if (storedId !== null) return storedId;
    }
  },
  roomName: (state) => state.roomName,
  confType: (state) => state.confType,
  isKicked: (state) => state.isKicked,
  isLeaving: (state) => state.isLeaving,
  isWaiting: (state) => state.isWaiting,
  isPasswordRequired: (state) => state.passwordRequired,
  isInvalid: (state) => state.isInvalid,
  invalidReason: (state) => state.invalidReason,

  isRejected: (state) => state.rejected,

  currentPendingGuest: (state) => state.currentPendingGuest,

  isGuestPage: (state, getters, rootState, rootGetters) =>
    rootGetters['current_user/isGuest'] && rootGetters['routing/routeName'] === 'room' && !rootGetters['isOpenSpace']
};

const mutations = {
  AREA_ID: (state, value) => (state.areaId = value),
  ROOM_ID: (state, value) => {
    state.roomId = value;
    // save to local storage so we can refer to it if the user navigates directly to an URL
    localStorage.setItem('guestInviteRoomId', value);
  },
  ROOM_NAME: (state, value) => (state.roomName = value),
  CONF_TYPE: (state, value) => (state.confType = value),
  PASSWORD_REQUIRED: (state, value) => (state.passwordRequired = value),
  LEAVING: (state, value) => (state.isLeaving = value),
  KICKED: (state, value) => (state.isKicked = value),
  WAITING: (state, value) => (state.isWaiting = value),
  INVALID: (state, value) => (state.isInvalid = value),
  INVALID_REASON: (state, value) => (state.invalidReason = value),

  REJECTED: (state, value) => (state.rejected = value),

  CURRENT_PENDING_GUEST: (state, value) => (state.currentPendingGuest = value)
};

const actions = {
  async handleAccessLobby({ rootGetters, dispatch, commit }, roomId) {
    //check link access on page load
    if (!roomId) {
      commit('INVALID', true);
      commit('INVALID_REASON', 'no_token_provided');
      return;
    }
    let res = await dispatch('api/nav/accessGuestLink', roomId, { root: true });
    let data = res?.success;
    if (!data && rootGetters['api/auth/isLoggedIn']) {
      //try to load room data if member refreshed (as guest acceess will fail if not premium space)
      res = await dispatch('api/nav/getRoom', roomId, { root: true });
      if (res?.success) {
        data = {
          userId: res.success.whereabouts?.userId,
          locationId: roomId,
          roomName: res.success.location.name,
          conferenceType: res.success.location.conferenceType,
          isExternalGuest: res.success.isExternalGuest
        };
      }
    }
    if (data) {
      commit('guest/USER_ID', data.userId, { root: true });
      commit('ROOM_ID', data.locationId);
      commit('ROOM_NAME', data.roomName);
      commit('CONF_TYPE', data.conferenceType);
      commit('INVALID', false);
      commit('current_user/EXTERNAL_GUEST', data.isExternalGuest, { root: true });
    } else {
      commit('INVALID', true);
      if (res.error) {
        commit('current_user/EXTERNAL_GUEST', res.error.isExternalGuest, { root: true });
        if (res.error.status == 404) commit('INVALID_REASON', 'not_found');
        else commit('INVALID_REASON', res.error.code);
      }
    }

    return res;
  },
  async handleEnterLobby({ getters, commit, dispatch }, username) {
    commit('guest/USERNAME', username, { root: true });
    //guest create login
    await dispatch('guest/createGuestAccount', [username, getters.roomId], { root: true });

    if (!getters.roomId) return dispatch('popups/guest/errorEnteringGuestLobby', null, { root: true });
    //enter lobby
    let res = await dispatch('api/nav/enterGuestRoomLobby', getters.roomId, { root: true });

    dispatch('analytics/enterRoomLobbyAsGuest', [username, getters.roomId, res], { root: true });

    let data = res?.success;
    if (data) await dispatch('updateData', data);
    else dispatch('popups/guest/errorEnteringGuestLobby', null, { root: true });
    commit('guest/ENTER_AFTER_AUTH', false, { root: true });
    commit('guest/REJOIN_AFTER_AUTH', false, { root: true });

    return res;
  },
  async handleEnterLobbyWithPassword({ dispatch, getters }, [password, showError = true]) {
    let res = await dispatch('api/nav/passwordJoinGuestLobby', [getters.roomId, password], { root: true });

    let data = res?.success;
    dispatch('analytics/enterRoomLobbyWithPassword', [getters.roomId, res], { root: true });
    if (data) dispatch('updateData', data);
    if ((!data || data?.passwordRequired) && showError)
      dispatch('popups/show/error/custom', 'entering the Room lobby or the password is incorrect', { root: true });

    return res;
  },
  async handleEnterRoomWithPassword({ getters, commit, dispatch }, [password, username]) {
    //enter room with password without joining lobby?
    commit('guest/USERNAME', username, { root: true });
    //guest create login
    await dispatch('guest/createGuestAccount', username, { root: true });

    if (!getters.roomId) return dispatch('popups/guest/errorEnteringGuestLobby', null, { root: true });

    let res = dispatch('handleEnterLobbyWithPassword', [password, false]);
    commit('guest/ENTER_AFTER_AUTH', false, { root: true });
    commit('guest/REJOIN_AFTER_AUTH', false, { root: true });

    return res;
  },
  async handleCancelJoinLobby({ dispatch, commit, getters }) {
    let res = await dispatch('api/nav/cancelJoinGuestLobby', getters.roomId, { root: true });

    let data = res?.success;
    if (data) {
      dispatch('updateData', data);
      commit('WAITING', false); //doing this for immediate response but actual response/success comes from message queue update
    } else dispatch('popups/show/error/custom', 'leaving the Room lobby', { root: true });

    return res;
  },
  async updateData({ commit, dispatch }, data) {
    if (!data) return console.log('NO DATA');
    commit('AREA_ID', data.areaId);
    commit('ROOM_ID', data.spaceId);
    commit('WAITING', data.onHold);
    commit('PASSWORD_REQUIRED', data.passwordRequired);

    //if onHold is false, enter room
    await dispatch('enterRoomOnUpdate');
    return;
  },
  async enterRoomOnUpdate({ getters, dispatch }) {
    //don't try to enter room unless not on hold and no password required (ie - after being accepted into room or after entering password)
    if (!getters.areaId || getters.isWaiting || getters.isPasswordRequired || (!getters.roomId && getters.isRejected))
      return;
    //attempt to enter area first (has to be before join for correct message queue updates) - allowed to fail (tests if area member - fails if guest or registered but non-member)
    await dispatch('enterAreaHandlerOnRoomLoad', getters.areaId, { root: true });
    await dispatch('handleJoinRoom', getters.roomId, { root: true });
  },
  returnToRoomLobby: async ({ rootGetters, commit, dispatch }, [kicked]) => {
    let res = await dispatch('handleLeaveRoomAndSpace', [false, kicked], { root: true });
    if (res?.success) {
      commit('KICKED', kicked);
      commit('LEAVING', true);
      await dispatch('showRoomLobbyHandler', null, { root: true });
      //only logout external guests
      if (rootGetters['current_user/isExternalGuest']) dispatch('api/auth/logoutAPI', null, { root: true });
    }
  },
  //message queue updates for lobby status/access to room
  handleRoomGuestUpdate({ rootGetters, getters, commit, dispatch }, [type, data]) {
    //types: ['userEntersLobby', 'userLeftLobby', 'hostAcceptedUserInLobby', 'hostDeclinedUserInLobby']
    if (data.userId !== rootGetters['current_user/id'] && data.locationId === rootGetters.getSpacePageId) {
      if (type === 'userEntersLobby') {
        console.log('refresh parts');
        setTimeout(() => dispatch('search/refreshSpaceParticipants', null, { root: true }), 2000);

        let isRoomPasswordEnabled = rootGetters.getSpaceLocationData?.passwordEnabled;
        if (isRoomPasswordEnabled === false || data.locationId === rootGetters.getCurrentLocationId) {
          if (getters.currentPendingGuest == null) {
            dispatch('modal/open', [PendingGuest], { root: true });
          }
        }

        commit('CURRENT_PENDING_GUEST', data);
      } else if (type === 'userLeftLobby') {
        //doing this because calling refresh participants is too slow
        dispatch('search/removeFromSpaceParticipants', data.userId, { root: true });
        dispatch('handleGuestNotInLobby', data);
      } else if (type === 'hostAcceptedUserInLobby' || type === 'hostDeclinedUserInLobby') {
        dispatch('handleGuestNotInLobby', data);
      }

      return;
    }

    if (rootGetters['current_user/id'] !== data.userId) return;
    if (type === 'userLeftLobby') {
      commit('WAITING', false);
    } else if (type === 'userEntersLobby') {
      commit('WAITING', true);
    } else if (type === 'hostAcceptedUserInLobby') {
      commit('WAITING', false);
      commit('PASSWORD_REQUIRED', false);
      dispatch('enterRoomOnUpdate');
    } else if (type === 'hostDeclinedUserInLobby') {
      commit('WAITING', false);
      commit('REJECTED', true);
    }
  },
  handleGuestNotInLobby({ commit, getters }, data) {
    // If the guest has left the lobby by themself, or have been accepted/declined
    if (!data || !getters.currentPendingGuest) return;

    if (getters.currentPendingGuest.userId === data.userId && getters.currentPendingGuest.locationId === data.locationId) {
      // This will close any current pending guest popup
      commit('CURRENT_PENDING_GUEST', null);
    }
  },
  resetRoomLobby({ commit }) {
    //if join room fails from room lobby
    commit('WAITING', false);
  },
  refreshLobby({ commit }) {
    //reset all to original as if refreshing page
    commit('LEAVING', false);
    commit('KICKED', false);
    commit('WAITING', false);
    commit('INVALID', false);
    commit('INVALID_REASON', null);
    commit('REJECTED', false);
    commit('guest/ENTER_AFTER_AUTH', false, { root: true });
    commit('guest/REJOIN_AFTER_AUTH', false, { root: true });
    commit('CURRENT_PENDING_GUEST', false);
  },
  async joinRoomAsExistingGuest({ rootGetters, dispatch }, type) {
    //type for redirect/callback
    let guest = rootGetters['guest/guestStorageData'];
    if (guest?.userId && guest?.oneTimeSignInToken) {
      await dispatch('api/auth/loginAsGuest', [guest.userId, guest.oneTimeSignInToken, type], { root: true });
      dispatch('analytics/joinRoomAsExistingGuest', [guest.userId, guest.oneTimeSignInToken, type], { root: true });
    }
    return;
  }
};

export default {
  state,
  getters,
  mutations,
  actions
};
